import { supabase } from './supabase.js';
import { insightsWindow } from './adminInsights.js';

/* ── ORDERS ── */

export async function fetchAllOrders({ status = null, page = 0, pageSize = 50 } = {}) {
  let q = supabase
    .from('orders')
    .select('*, order_items(*)')
    // Only orders the customer actually paid for are real orders. Unpaid
    // rows are abandoned checkouts (customer closed the Razorpay modal) and
    // must never appear in the admin panel. Refunds stay visible.
    .in('payment_status', ['paid', 'refunded'])
    .order('created_at', { ascending: false })
    .range(page * pageSize, page * pageSize + pageSize - 1);

  if (status) q = q.eq('status', status);

  const { data, error } = await q;
  return { data: data ?? [], error };
}

export async function updateOrderStatus(orderId, status) {
  const { data, error } = await supabase
    .from('orders')
    .update({ status })
    .eq('id', orderId)
    .select()
    .single();
  return { data, error };
}

export async function fetchOrderDetail(orderId) {
  const { data, error } = await supabase
    .from('orders')
    .select('*, order_items(*)')
    .eq('id', orderId)
    .single();
  return { data, error };
}

export async function updateOrderAdminNotes(orderId, notes) {
  const { data, error } = await supabase
    .from('orders')
    .update({ admin_notes: notes && notes.trim() ? notes.trim() : null })
    .eq('id', orderId)
    .select()
    .single();
  return { data, error };
}

/**
 * Update the shipping / tracking fields on an order. Pass null for any
 * field you want to clear. The DB trigger orders_stamp_shipped_at handles
 * the `shipped_at` timestamp automatically when status flips to 'shipped'.
 */
export async function updateOrderTracking(orderId, { carrier, tracking_number, tracking_url }) {
  const clean = (v) => (typeof v === 'string' && v.trim()) ? v.trim() : null;
  const payload = {
    carrier:         clean(carrier),
    tracking_number: clean(tracking_number),
    tracking_url:    clean(tracking_url),
  };
  const { data, error } = await supabase
    .from('orders')
    .update(payload)
    .eq('id', orderId)
    .select()
    .single();
  return { data, error };
}

/**
 * WhatsApp the customer about an order status change, via the
 * `send-order-whatsapp` Edge Function (self-hosted OpenWA gateway).
 * Message copy lives in `supabase/functions/_shared/order-whatsapp.ts`;
 * no Meta template approval is involved.
 *
 * `event` is one of: 'confirmed' | 'shipped' | 'delivered' |
 * 'cancelled' | 'refunded'. Returns `{ data, error }`; callers should
 * surface the error inline but not block the underlying admin action
 * (status update, refund) — WhatsApp delivery is a best-effort side
 * channel, not a transactional dependency.
 */
export async function sendOrderWhatsApp(orderId, event) {
  const { data, error } = await supabase.functions.invoke('send-order-whatsapp', {
    body: { order_id: orderId, event },
  });
  if (error) {
    const serverMsg = (data && data.error) || error.message || 'WhatsApp send failed';
    return { data: null, error: new Error(serverMsg) };
  }
  if (data && data.error) {
    return { data: null, error: new Error(data.error) };
  }
  return { data, error: null };
}

/**
 * Email the customer about an order status change via the
 * `send-order-email` Edge Function. `event` is one of 'shipped' |
 * 'delivered' | 'cancelled' | 'refunded' — 'confirmed' is excluded on
 * purpose because verify-razorpay-payment already sends it the moment
 * payment clears.
 *
 * Same contract as sendOrderWhatsApp: best-effort side channel, callers
 * should log the error but never block the admin action on it.
 */
export async function sendOrderEmail(orderId, event) {
  const { data, error } = await supabase.functions.invoke('send-order-email', {
    body: { order_id: orderId, event },
  });
  if (error) {
    const serverMsg = (data && data.error) || error.message || 'Email send failed';
    return { data: null, error: new Error(serverMsg) };
  }
  if (data && data.error) {
    return { data: null, error: new Error(data.error) };
  }
  return { data, error: null };
}

/**
 * SMS the customer about an order status change via the `send-order-sms`
 * Edge Function (MSG91). `event` is one of 'shipped' | 'delivered' |
 * 'cancelled' | 'refunded' — 'confirmed' is excluded for the same reason as
 * email: verify-razorpay-payment already sends it when payment clears.
 *
 * Same contract as sendOrderEmail: best-effort side channel, callers should
 * log the error but never block the admin action on it.
 */
export async function sendOrderSms(orderId, event) {
  const { data, error } = await supabase.functions.invoke('send-order-sms', {
    body: { order_id: orderId, event },
  });
  if (error) {
    const serverMsg = (data && data.error) || error.message || 'SMS send failed';
    return { data: null, error: new Error(serverMsg) };
  }
  if (data && data.error) {
    return { data: null, error: new Error(data.error) };
  }
  return { data, error: null };
}

/**
 * Issue a Razorpay refund for an order via the `refund-razorpay-payment`
 * Edge Function. Caller must be signed in as an admin; the function
 * re-checks `is_admin` server-side. Pass `amount` in rupees to partial-
 * refund, or omit to refund the full order total.
 */
export async function refundOrder(orderId, { amount, notes } = {}) {
  const payload = { order_id: orderId };
  if (amount != null && amount !== '') payload.amount = Number(amount);
  if (notes) payload.notes = notes;
  const { data, error } = await supabase.functions.invoke('refund-razorpay-payment', {
    body: payload,
  });
  if (error) {
    // supabase-js wraps non-2xx responses in `error`; surface the server's
    // JSON error body when present so the UI can show a useful message.
    const serverMsg = (data && data.error) || error.message || 'Refund failed';
    return { data: null, error: new Error(serverMsg) };
  }
  if (data && data.error) {
    return { data: null, error: new Error(data.error) };
  }
  return { data, error: null };
}

/* ── PRODUCTS ── */

export async function fetchAllProductsAdmin() {
  const [prodsRes, varsRes, bundleRes] = await Promise.all([
    supabase
      .from('products')
      .select('*, categories(name, image_url)')
      .order('id', { ascending: true }),
    supabase.from('product_variants').select('*').order('sort_order', { ascending: true }),
    supabase.from('bundle_items').select('*').order('sort_order', { ascending: true }),
  ]);
  if (prodsRes.error) return { data: [], error: prodsRes.error };
  const variants = varsRes.error ? [] : (varsRes.data || []);
  const bundleItems = bundleRes.error ? [] : (bundleRes.data || []);
  const byProd = new Map();
  for (const v of variants) {
    if (!byProd.has(v.product_id)) byProd.set(v.product_id, []);
    byProd.get(v.product_id).push(v);
  }
  const bundleByProd = new Map();
  for (const b of bundleItems) {
    if (!bundleByProd.has(b.bundle_product_id)) bundleByProd.set(b.bundle_product_id, []);
    bundleByProd.get(b.bundle_product_id).push(b);
  }
  const data = (prodsRes.data || []).map(p => ({
    ...p,
    variants: byProd.get(p.id) || [],
    bundle_items: bundleByProd.get(p.id) || [],
  }));
  return { data, error: null };
}

/**
 * Fetch the variants for a single product. Used by the admin product editor
 * when opening an existing product so it can pre-fill the variants section.
 */
export async function fetchProductVariants(productId) {
  const { data, error } = await supabase
    .from('product_variants')
    .select('*')
    .eq('product_id', productId)
    .order('sort_order', { ascending: true });
  return { data: data ?? [], error };
}

/**
 * Replace the variants for a product. The editor sends the FULL desired set
 * — rows with an `id` get UPDATEd in place, rows without an id get INSERTed,
 * and any existing variant whose id is no longer in the set gets DELETEd.
 * Caller is responsible for setting products.has_variants on the product row.
 *
 * `variants`: array of { id?, label, mrp, sale_price, stock_qty,
 *   low_stock_threshold, main_image_url, gallery_image_urls, sort_order }
 */
export async function saveProductVariants(productId, variants) {
  const num   = (v) => v === '' || v == null ? null : Number(v);
  const arr   = (v) => Array.isArray(v) ? v.filter(Boolean) : [];
  const int   = (v, def = 0) => v === '' || v == null ? def : (parseInt(v, 10) || def);

  // 1. Read the current set so we know what to delete.
  const { data: existingRows, error: readErr } = await supabase
    .from('product_variants').select('id').eq('product_id', productId);
  if (readErr) return { error: readErr };

  const desiredIds = new Set(variants.filter(v => v.id).map(v => v.id));
  const toDelete = (existingRows || []).filter(r => !desiredIds.has(r.id)).map(r => r.id);

  // 2. Delete first (so a label that's being reused on a different row
  //    doesn't collide with the unique (product_id, label) index).
  if (toDelete.length) {
    const { error } = await supabase
      .from('product_variants').delete().in('id', toDelete);
    if (error) return { error };
  }

  // 3. Upsert the desired rows. Build payloads carefully — sale_price is
  //    required by the schema, label is required by the unique index.
  //    New rows (no id) and existing rows (has id) are sent in SEPARATE
  //    calls: PostgREST builds one INSERT with a unified column set per
  //    batch, so mixing them would send an explicit NULL for `id` on the
  //    new rows instead of omitting the column — which bypasses the
  //    `default gen_random_uuid()` and trips the not-null constraint.
  const newRows = [];
  const existingRows2 = [];
  variants.forEach((v, idx) => {
    const row = {
      product_id:          productId,
      label:               (v.label || '').trim(),
      mrp:                 num(v.mrp),
      sale_price:          num(v.sale_price),
      stock_qty:           Math.max(0, int(v.stock_qty, 0)),
      low_stock_threshold: Math.max(0, int(v.low_stock_threshold, 5)),
      main_image_url:      (typeof v.main_image_url === 'string' && v.main_image_url.trim()) || null,
      gallery_image_urls:  arr(v.gallery_image_urls),
      sort_order:          int(v.sort_order, idx),
    };
    if (v.id) {
      existingRows2.push({ ...row, id: v.id });
    } else {
      newRows.push(row);
    }
  });

  if (newRows.length) {
    const { error } = await supabase.from('product_variants').insert(newRows);
    if (error) return { error };
  }

  if (existingRows2.length) {
    const { error } = await supabase
      .from('product_variants')
      .upsert(existingRows2, { onConflict: 'id' });
    if (error) return { error };
  }

  return { error: null };
}

export async function upsertProduct(product) {
  // The DB's products_sync_legacy_fields trigger keeps `price` /
  // `original_price` / `in_stock` in sync with the new fields, but for
  // clarity we still send the legacy columns when only the legacy form
  // sent them. Modern callers send `sale_price` + `mrp` + `stock_qty`.
  const num   = (v) => v === '' || v == null ? null : Number(v);
  const arr   = (v) => Array.isArray(v) ? v.filter(Boolean) : null;
  const text  = (v) => (typeof v === 'string' && v.trim()) ? v.trim() : null;

  const salePrice = num(product.sale_price ?? product.price);
  const mrp       = num(product.mrp        ?? product.original_price);
  const stockQty  = product.stock_qty != null
    ? Math.max(0, parseInt(product.stock_qty, 10) || 0)
    : (product.in_stock === false ? 0 : 0); // legacy form: just 0 when no qty given

  const payload = {
    id: product.id || undefined,
    category_id: product.category_id ? parseInt(product.category_id, 10) : null,
    name: product.name,
    subtitle: text(product.subtitle),
    short_description: text(product.short_description),
    description: text(product.description),
    how_to_use: text(product.how_to_use),
    sale_price: salePrice,
    mrp,
    // Legacy columns also sent for safety on installs that haven't run
    // the migration yet. The trigger overwrites these once it exists.
    price: salePrice,
    original_price: mrp,
    weight: text(product.weight),
    badge: text(product.badge),
    img_key: product.img_key || 'supplement',
    photo: text(product.photo),
    stock_qty: stockQty,
    low_stock_threshold: product.low_stock_threshold != null
      ? Math.max(0, parseInt(product.low_stock_threshold, 10) || 0)
      : undefined,
    main_image_url: text(product.main_image_url),
    gallery_image_urls: arr(product.gallery_image_urls) ?? undefined,
    tags: arr(product.tags) ?? undefined,
    slug: text(product.slug),
    meta_title: text(product.meta_title),
    meta_description: text(product.meta_description),
    has_variants: product.has_variants === true ? true : (product.has_variants === false ? false : undefined),
    is_bundle: product.is_bundle === true ? true : (product.is_bundle === false ? false : undefined),
    free_shipping: product.free_shipping === true ? true : (product.free_shipping === false ? false : undefined),
  };
  if (!payload.id) delete payload.id;
  // Strip undefined so we don't blank existing values on update.
  Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

  const { data, error } = await supabase
    .from('products')
    .upsert(payload)
    .select()
    .single();
  return { data, error };
}

export async function deleteProduct(id) {
  const { error } = await supabase.from('products').delete().eq('id', id);
  return { error };
}

/* ── IMAGE UPLOAD (auto-converts to WebP) ── */

// Re-encode any image File to a WebP Blob, downscaled so the longest edge
// is <= maxDim. Throws if the browser can't decode the file.
async function fileToWebpBlob(file, { maxDim = 1200, quality = 0.85 } = {}) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Could not decode image'));
      i.src = url;
    });
    let w = img.naturalWidth, h = img.naturalHeight;
    const longest = Math.max(w, h);
    if (longest > maxDim) {
      const scale = maxDim / longest;
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        b => b ? resolve(b) : reject(new Error('WebP encoding failed (browser unsupported?)')),
        'image/webp',
        quality,
      );
    });
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

const PRODUCT_IMG_BRAND = 'sunnahenterprises';

// Slug base for branded image filenames. Regex kept identical to the `slugify`
// helper in script.js:319 (keep both in sync). Capped at 80 chars so storage
// keys stay sane, with a 'product' fallback for empty/degenerate names.
function slugifyName(s) {
  const slug = String(s ?? '')
    .toLowerCase().trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const capped = slug.slice(0, 80).replace(/-+$/g, '');
  return capped || 'product';
}

// `suffix` is '' for the first attempt, then '-2'/'-3'/… or '-<token>' on collision.
function brandedKey(base, suffix) {
  return `${base}-${PRODUCT_IMG_BRAND}${suffix}.webp`;
}

// supabase-js v2 surfaces upload conflicts on the resolved { error } (no throw):
// error.statusCode === '409', message === 'The resource already exists'.
function isAlreadyExists(err) {
  const msg = String(err?.message || '').toLowerCase();
  const code = String(err?.statusCode ?? err?.status ?? '');
  return code === '409' || msg.includes('already exists') || msg.includes('duplicate');
}

/**
 * Upload a product image to the `product-images` bucket. Auto-converts
 * to WebP and downscales to 1600px max edge. Returns { url } on success.
 * @param {File} file
 * @param {object} opts
 * @param {string|number} [opts.productId] — for the legacy storage path; falls back to a timestamp
 * @param {string} [opts.kind] — 'main' | 'gallery' (used in the legacy filename for traceability)
 * @param {string} [opts.name] — when the key is supplied (even blank), the object is stored under an
 *   SEO filename `<slug>-sunnahenterprises.webp` (blank/whitespace slugs to the `product` fallback;
 *   `-2`/`-3`/… on collision). When the key is omitted entirely, the legacy
 *   `prod-<id>-<kind>-<ts>.webp` scheme is used (hero/blog callers rely on this).
 */
export async function uploadProductImage(file, { productId = 'new', kind = 'main', name } = {}) {
  let webp;
  try {
    webp = await fileToWebpBlob(file, { maxDim: 1600, quality: 0.85 });
  } catch (e) {
    return { error: e };
  }

  const uploadTo = p => supabase.storage
    .from('product-images')
    .upload(p, webp, { contentType: 'image/webp', upsert: false, cacheControl: '31536000' });

  let path, upErr;
  // Branch on whether the caller supplied the `name` key at all (product/variant callers always
  // do, even when the field is blank → 'product' fallback slug). Hero/blog callers omit it → legacy.
  if (name !== undefined) {
    // Branded path: clean name first, then numeric suffixes on 409, then a timestamp fallback.
    const base = slugifyName(name);
    const MAX_SUFFIX = 25;
    for (let n = 1; ; n++) {
      if (n === 1) path = brandedKey(base, '');
      else if (n <= MAX_SUFFIX) path = brandedKey(base, `-${n}`);
      else path = brandedKey(base, `-${Date.now()}`);
      ({ error: upErr } = await uploadTo(path));
      if (!upErr) break;
      if (isAlreadyExists(upErr) && n <= MAX_SUFFIX) continue; // try next suffix
      if (isAlreadyExists(upErr)) {
        // Even the timestamp key clashed (≈impossible) — add a random token and stop.
        path = brandedKey(base, `-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
        ({ error: upErr } = await uploadTo(path));
        break;
      }
      break; // real error → fall through to friendly-message mapping
    }
  } else {
    // Legacy path (hero slides, blog covers, anything without a name) — unchanged.
    const safeId = String(productId).replace(/[^a-z0-9-]+/gi, '-').toLowerCase() || 'new';
    path = `prod-${safeId}-${kind}-${Date.now()}.webp`;
    ({ error: upErr } = await uploadTo(path));
  }

  if (upErr) {
    const msg = (upErr.message || '').toLowerCase();
    if (msg.includes('row-level security') || msg.includes('not authorized') || msg.includes('unauthorized')) {
      return { error: new Error('Upload denied by storage policy. Run supabase/migrate_product_fields.sql and make sure your account has admin access.') };
    }
    if (msg.includes('bucket not found') || msg.includes('does not exist')) {
      return { error: new Error('Storage bucket "product-images" not found. Run supabase/migrate_product_fields.sql in the Supabase SQL Editor.') };
    }
    return { error: upErr };
  }
  const { data } = supabase.storage.from('product-images').getPublicUrl(path);
  return { url: data.publicUrl };
}

/**
 * Upload a category image to the `category-images` bucket. Auto-converts to WebP.
 * When `name` is supplied (even blank), the object is stored under an SEO filename
 * `<slug>-sunnahenterprises.webp` (blank/whitespace → `product` fallback slug; `-2`/`-3`/…
 * on collision). When `name` is omitted, the legacy `cat-<id>-<ts>.webp` scheme is used.
 */
export async function uploadCategoryImage(file, categoryId, name) {
  let webp;
  try {
    webp = await fileToWebpBlob(file);
  } catch (e) {
    return { error: e };
  }

  const uploadTo = p => supabase.storage
    .from('category-images')
    .upload(p, webp, { contentType: 'image/webp', upsert: false, cacheControl: '31536000' });

  let path, upErr;
  if (name !== undefined) {
    // Branded path: clean name first, then numeric suffixes on 409, then a timestamp fallback.
    const base = slugifyName(name);
    const MAX_SUFFIX = 25;
    for (let n = 1; ; n++) {
      if (n === 1) path = brandedKey(base, '');
      else if (n <= MAX_SUFFIX) path = brandedKey(base, `-${n}`);
      else path = brandedKey(base, `-${Date.now()}`);
      ({ error: upErr } = await uploadTo(path));
      if (!upErr) break;
      if (isAlreadyExists(upErr) && n <= MAX_SUFFIX) continue; // try next suffix
      if (isAlreadyExists(upErr)) {
        path = brandedKey(base, `-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
        ({ error: upErr } = await uploadTo(path));
        break;
      }
      break; // real error → fall through to friendly-message mapping
    }
  } else {
    // Legacy path — unchanged.
    path = `cat-${categoryId}-${Date.now()}.webp`;
    ({ error: upErr } = await uploadTo(path));
  }

  if (upErr) {
    const msg = (upErr.message || '').toLowerCase();
    if (msg.includes('row-level security') || msg.includes('not authorized') || msg.includes('unauthorized')) {
      return { error: new Error('Upload denied by storage policy. Run supabase/storage_setup.sql and make sure your account has admin access.') };
    }
    if (msg.includes('bucket not found') || msg.includes('does not exist')) {
      return { error: new Error('Storage bucket "category-images" not found. Run supabase/storage_setup.sql in the Supabase SQL Editor.') };
    }
    return { error: upErr };
  }
  const { data } = supabase.storage.from('category-images').getPublicUrl(path);
  return { url: data.publicUrl };
}

/* ── CATEGORIES ── */

export async function fetchAllCategoriesAdmin() {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .order('sort_order', { ascending: true });
  return { data: data ?? [], error };
}

export async function upsertCategory(cat) {
  const { data, error } = await supabase
    .from('categories')
    .upsert({
      id: cat.id,
      name: cat.name,
      image_url: cat.image_url ?? null,
      sort_order: cat.sort_order ?? 0,
    })
    .select()
    .single();
  return { data, error };
}

export async function deleteCategory(id) {
  // RLS-denied deletes return error:null with 0 rows (the policy's USING filters
  // them invisibly). Chain .select() and synthesize an error on an empty result
  // so a blocked delete surfaces instead of false-positiving as success.
  const { data, error } = await supabase.from('categories').delete().eq('id', id).select('id');
  if (!error && (!data || data.length === 0)) {
    return { error: new Error('Delete blocked — no rows removed. Has the categories RLS migration been applied?') };
  }
  return { error };
}

// Live count of products tagged to a category (head-only, no rows fetched).
// Used to warn before delete that N products will become uncategorized.
export async function countProductsInCategory(id) {
  const { count, error } = await supabase
    .from('products')
    .select('id', { count: 'exact', head: true })
    .eq('category_id', id);
  return { count: count ?? 0, error };
}

/* ── PAYMENTS ──
 * Pulls every paid order from the last 90 days so the Payments admin tab
 * can compute Today / This Week / This Month / All Time KPIs, a 14-day
 * daily breakdown, and a recent-payments table. Cancelled orders are
 * excluded; refunds (payment_status='refunded') stay in the result so
 * we can show them as a separate badge. */
export async function fetchPaymentsOverview({ days = 90 } = {}) {
  const since = new Date();
  since.setDate(since.getDate() - days);
  const { data, error } = await supabase
    .from('orders')
    .select(
      'id, total, discount_amount, created_at, payment_status, payment_method, ' +
      'razorpay_payment_id, razorpay_order_id, full_name, email, phone, status'
    )
    .in('payment_status', ['paid', 'refunded'])
    .neq('status', 'cancelled')
    .gte('created_at', since.toISOString())
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}

// PostgREST caps one response at 1000 rows; page through so long windows are not silently cut short.
async function selectAll(build) {
  const out = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await build().range(from, from + 999);
    if (error) return { data: out, error };
    out.push(...data);
    if (data.length < 1000) return { data: out, error: null };
  }
}

/** Raw rows for the admin Dashboard (the maths is in adminInsights.js). `days` 0 = today, else 7 / 30 / 90. */
export async function fetchInsightsData({ days = 30 } = {}) {
  const w = insightsWindow(days);
  const prevIso = new Date(w.prevSince).toISOString();
  const sinceIso = new Date(w.since).toISOString();
  const [orders, items, profiles, customers, cats, prods, low, oos, toShip, msgs, revs, recent] = await Promise.all([
    selectAll(() => supabase.from('orders').select('id, user_id, status, payment_status, total, state, created_at')
      .gte('created_at', prevIso).order('created_at').order('id')),
    // Lines of paid, non-cancelled orders in the current window only (same revenue rule as above).
    selectAll(() => supabase.from('order_items')
      .select('id, product_id, product_name, quantity, line_total, orders!inner(created_at, status, payment_status)')
      .eq('orders.payment_status', 'paid').neq('orders.status', 'cancelled').gte('orders.created_at', sinceIso).order('id')),
    selectAll(() => supabase.from('profiles').select('id, created_at').gte('created_at', prevIso).order('created_at').order('id')),
    supabase.from('profiles').select('id', { count: 'exact', head: true }),
    supabase.from('categories').select('id, name'),
    supabase.from('products').select('id, category_id'),
    fetchLowStockProducts(),
    fetchOutOfStockProducts(),
    supabase.from('orders').select('id', { count: 'exact', head: true }).eq('payment_status', 'paid').in('status', ['pending', 'confirmed']),
    countUnreadMessages(),
    countPendingReviews(),
    fetchAllOrders({ pageSize: 6 }),
  ]);
  return {
    error: orders.error || items.error,
    orders: orders.data, items: items.data, profiles: profiles.data,
    customerCount: customers.count ?? 0,
    categories: cats.data ?? [], products: prods.data ?? [],
    lowStock: low.data, outOfStock: oos.data, recentOrders: recent.data,
    counts: { toShip: toShip.count ?? 0, messages: msgs.count, reviews: revs.count },
  };
}

/* ── USERS / PROFILES ── */

export async function fetchAllProfiles() {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}

export async function updateProfileAdmin(id, updates) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

/* ── CUSTOMERS (CRM) ── */

// Aggregate row per customer: order_count, lifetime_spend, AOV, last_ordered_at.
// Backed by the `customer_summaries` view in supabase/migrate_admin_crm.sql.
export async function fetchCustomerSummaries() {
  const { data, error } = await supabase
    .from('customer_summaries')
    .select('*')
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}

export async function fetchCustomerDetail(userId) {
  const [profileRes, summaryRes] = await Promise.all([
    supabase.from('profiles').select('*').eq('id', userId).single(),
    supabase.from('customer_summaries').select('*').eq('id', userId).maybeSingle(),
  ]);
  return {
    profile: profileRes.data,
    summary: summaryRes.data,
    error: profileRes.error || summaryRes.error,
  };
}

export async function fetchCustomerCart(userId) {
  const { data, error } = await supabase
    .from('cart_items')
    .select('*, products(id, name, photo, main_image_url, sale_price, price, stock_qty, in_stock)')
    .eq('user_id', userId)
    .order('added_at', { ascending: false });
  return { data: data ?? [], error };
}

export async function fetchCustomerOrders(userId) {
  const { data, error } = await supabase
    .from('orders')
    .select('*, order_items(*)')
    .eq('user_id', userId)
    // Same rule as fetchAllOrders: unpaid rows are abandoned checkouts.
    .in('payment_status', ['paid', 'refunded'])
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}

export async function fetchCustomerMessages(userId) {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}

// Distinct delivery addresses pulled from this customer's order history.
// Returns an array of { address_line1, address_line2, city, state, pincode, last_used }.
export async function fetchCustomerAddresses(userId) {
  const { data, error } = await supabase
    .from('orders')
    .select('address_line1, address_line2, city, state, pincode, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) return { data: [], error };
  const seen = new Map();
  for (const o of data || []) {
    const key = [o.address_line1, o.address_line2, o.city, o.state, o.pincode].join('|').toLowerCase();
    if (!seen.has(key)) {
      seen.set(key, {
        address_line1: o.address_line1,
        address_line2: o.address_line2,
        city: o.city,
        state: o.state,
        pincode: o.pincode,
        last_used: o.created_at,
      });
    }
  }
  return { data: Array.from(seen.values()), error: null };
}

export async function updateCustomerAdminNotes(profileId, notes) {
  const { data, error } = await supabase
    .from('profiles')
    .update({ admin_notes: notes && notes.trim() ? notes.trim() : null })
    .eq('id', profileId)
    .select()
    .single();
  return { data, error };
}

/* ── MESSAGES (contact-form enquiries) ── */

export async function fetchAllMessages({ status = null } = {}) {
  let q = supabase
    .from('messages')
    .select('*')
    .order('created_at', { ascending: false });
  if (status) q = q.eq('status', status);
  const { data, error } = await q;
  return { data: data ?? [], error };
}

export async function updateMessageStatus(id, status) {
  const { data, error } = await supabase
    .from('messages')
    .update({ status })
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

export async function deleteMessage(id) {
  const { error } = await supabase.from('messages').delete().eq('id', id);
  return { error };
}

export async function countUnreadMessages() {
  const { count, error } = await supabase
    .from('messages')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'new');
  return { count: count ?? 0, error };
}

export async function countPendingOrders() {
  const { count, error } = await supabase
    .from('orders')
    .select('id', { count: 'exact', head: true })
    // Same rule as fetchOrders: unpaid rows are abandoned checkouts, not orders.
    .in('payment_status', ['paid', 'refunded'])
    .eq('status', 'pending');
  return { count: count ?? 0, error };
}

/** Fetch customer wishlists (for admin customer drawer). */
export async function fetchCustomerWishlist(userId) {
  const { data, error } = await supabase
    .from('wishlists')
    .select('product_id, added_at')
    .eq('user_id', userId)
    .order('added_at', { ascending: false });
  return { data: data || [], error };
}

/** Fetch low-stock products (stock_qty > 0 but <= low_stock_threshold). */
export async function fetchLowStockProducts() {
  const { data, error } = await supabase
    .from('products')
    .select('id, name, slug, stock_qty, low_stock_threshold, sale_price, main_image_url, photo')
    .gt('stock_qty', 0)
    .order('stock_qty', { ascending: true });
  if (error) return { data: [], error };
  // Filter client-side: stock_qty <= low_stock_threshold
  const low = (data || []).filter(p => p.stock_qty <= (p.low_stock_threshold ?? 5));
  return { data: low, error: null };
}

/** Fetch out-of-stock products. */
export async function fetchOutOfStockProducts() {
  const { data, error } = await supabase
    .from('products')
    .select('id, name, slug, stock_qty, sale_price, main_image_url, photo')
    .lte('stock_qty', 0)
    .order('name');
  return { data: data || [], error };
}

/** Quick restock — set stock_qty for a product. */
export async function restockProduct(productId, newQty) {
  const { error } = await supabase
    .from('products')
    .update({ stock_qty: newQty })
    .eq('id', productId);
  return { error };
}

/* ── COUPONS ── */

export async function fetchAllCoupons() {
  // Admin RLS policy on coupons covers inactive rows too.
  const { data, error } = await supabase
    .from('coupons')
    .select('*')
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}

export async function fetchCoupon(id) {
  const { data, error } = await supabase
    .from('coupons')
    .select('*')
    .eq('id', id)
    .single();
  return { data, error };
}

export async function fetchCouponRedemptionCounts(couponId) {
  const { count, error } = await supabase
    .from('coupon_redemptions')
    .select('id', { count: 'exact', head: true })
    .eq('coupon_id', couponId);
  return { count: count ?? 0, error };
}

const num   = (v) => v === '' || v == null ? null : Number(v);
const text  = (v) => (typeof v === 'string' && v.trim()) ? v.trim() : null;

export async function upsertCoupon(coupon) {
  const payload = {
    id: coupon.id || undefined,
    code: (coupon.code || '').trim(),
    description: text(coupon.description),
    kind: coupon.kind,
    value: coupon.kind === 'free_shipping' ? null : num(coupon.value),
    scope: coupon.scope || 'cart',
    category_id: coupon.scope === 'category'
      ? (coupon.category_id ? parseInt(coupon.category_id, 10) : null)
      : null,
    min_subtotal: num(coupon.min_subtotal) ?? 0,
    max_discount: num(coupon.max_discount),
    usage_limit: coupon.usage_limit === '' || coupon.usage_limit == null
      ? null : parseInt(coupon.usage_limit, 10),
    per_user_limit: coupon.per_user_limit === '' || coupon.per_user_limit == null
      ? null : parseInt(coupon.per_user_limit, 10),
    first_order_only: !!coupon.first_order_only,
    starts_at: coupon.starts_at || null,
    expires_at: coupon.expires_at || null,
    active: coupon.active !== false,
  };
  if (!payload.id) delete payload.id;
  if (!payload.code) return { error: new Error('Coupon code is required') };

  const { data, error } = await supabase
    .from('coupons')
    .upsert(payload)
    .select()
    .single();
  return { data, error };
}

export async function deleteCoupon(id) {
  const { error } = await supabase.from('coupons').delete().eq('id', id);
  return { error };
}

/* ── CONTENT: HERO SLIDES / FEATURED / BLOG ── */

export async function fetchAllHeroSlides() {
  const { data, error } = await supabase
    .from('hero_slides')
    .select('*')
    .order('sort_order', { ascending: true });
  return { data: data ?? [], error };
}

export async function upsertHeroSlide(slide) {
  const payload = {
    id: slide.id || undefined,
    kind: slide.kind === 'split' ? 'split' : 'banner',
    image_url:      text(slide.image_url),
    alt_text:       text(slide.alt_text),
    link_view:      text(slide.link_view),
    eyebrow:        text(slide.eyebrow),
    title:          text(slide.title),
    subtitle:       text(slide.subtitle),
    price_label:    text(slide.price_label),
    original_label: text(slide.original_label),
    save_label:     text(slide.save_label),
    cta_label:      text(slide.cta_label),
    cta_target:     text(slide.cta_target),
    emoji:          text(slide.emoji),
    sort_order:     slide.sort_order == null ? 0 : parseInt(slide.sort_order, 10) || 0,
    active:         slide.active !== false,
  };
  if (!payload.id) delete payload.id;
  const { data, error } = await supabase
    .from('hero_slides').upsert(payload).select().single();
  return { data, error };
}

export async function deleteHeroSlide(id) {
  const { error } = await supabase.from('hero_slides').delete().eq('id', id);
  return { error };
}

export async function fetchFeaturedRows() {
  const { data, error } = await supabase
    .from('featured_products')
    .select('product_id, sort_order')
    .order('sort_order', { ascending: true });
  return { data: data ?? [], error };
}

/**
 * Replace the featured-products list with the provided ordered ids.
 * Simple strategy: delete the lot, insert the new set. Featured is a
 * small (~6 row) table so this is fine and keeps the code obvious.
 */
export async function replaceFeaturedProducts(productIds) {
  const ids = (productIds || []).map(x => parseInt(x, 10)).filter(Number.isInteger);
  const { error: delErr } = await supabase
    .from('featured_products').delete().not('product_id', 'is', null);
  if (delErr) return { error: delErr };
  if (!ids.length) return { error: null };
  const rows = ids.map((pid, i) => ({ product_id: pid, sort_order: i }));
  const { error: insErr } = await supabase
    .from('featured_products').insert(rows);
  return { error: insErr };
}

/* ── PINNED ITEMS (pin to top of the public products page) ── */

// Ordered by pinned_at asc — earliest pin first = topmost position.
export async function fetchPinnedItems() {
  const { data, error } = await supabase
    .from('pinned_items')
    .select('item_type, item_id, pinned_at')
    .order('pinned_at', { ascending: true });
  return { data: data ?? [], error };
}

// Pin or re-pin. Upsert on the (item_type,item_id) unique constraint and
// refresh pinned_at so a re-pin moves the item to the bottom of the section.
export async function pinItem(itemType, itemId) {
  const row = {
    item_type: itemType,
    item_id:   parseInt(itemId, 10),
    pinned_at: new Date().toISOString(),
  };
  const { data, error } = await supabase
    .from('pinned_items')
    .upsert(row, { onConflict: 'item_type,item_id' })
    .select()
    .single();
  return { data, error };
}

export async function unpinItem(itemType, itemId) {
  const { error } = await supabase
    .from('pinned_items')
    .delete()
    .eq('item_type', itemType)
    .eq('item_id', parseInt(itemId, 10));
  return { error };
}

export async function fetchAllBlogPosts() {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .order('published_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}

export async function upsertBlogPost(post) {
  const payload = {
    id: post.id || undefined,
    slug:          text(post.slug),
    title:         text(post.title),
    category:      text(post.category),
    excerpt:       text(post.excerpt),
    body:          text(post.body),
    cover_url:     text(post.cover_url),
    cover_fit:     post.cover_fit === 'contain' ? 'contain' : 'cover',
    cover_alt:        text(post.cover_alt),
    meta_title:       text(post.meta_title),
    meta_description: text(post.meta_description),
    author:           text(post.author),
    read_time_min: post.read_time_min == null || post.read_time_min === ''
      ? null : parseInt(post.read_time_min, 10),
    published_at:  post.published_at || null,
    sort_order:    post.sort_order == null ? 0 : parseInt(post.sort_order, 10) || 0,
    active:        post.active !== false,
  };
  if (!payload.id) delete payload.id;
  if (!payload.slug || !payload.title) {
    return { error: new Error('Blog post requires both a slug and a title') };
  }
  const { data, error } = await supabase
    .from('blog_posts').upsert(payload).select().single();
  return { data, error };
}

export async function deleteBlogPost(id) {
  const { error } = await supabase.from('blog_posts').delete().eq('id', id);
  return { error };
}

/* ── REVIEWS (moderation) ── */

/**
 * Admin list of all reviews, joined with product info so the table can
 * show what was reviewed without a per-row lookup. Optional status filter
 * supports the moderation chips ('pending' / 'approved' / 'hidden').
 */
export async function fetchAllReviewsAdmin({ status = null } = {}) {
  let q = supabase
    .from('reviews')
    .select(`*, products ( id, name, main_image_url, photo )`)
    .order('created_at', { ascending: false });
  if (status) q = q.eq('status', status);
  const { data: reviews, error } = await q;
  if (error) return { data: [], error };

  // Second pass: hydrate reviewer names from profiles (no FK between
  // reviews.user_id and profiles.id, so we can't embed via PostgREST).
  const userIds = Array.from(new Set((reviews || []).map(r => r.user_id).filter(Boolean)));
  let profileMap = new Map();
  if (userIds.length) {
    const { data: profs } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .in('id', userIds);
    profileMap = new Map((profs || []).map(p => [p.id, p]));
  }
  const data = (reviews || []).map(r => ({
    ...r,
    profile: profileMap.get(r.user_id) || null,
  }));
  return { data, error: null };
}

export async function updateReviewStatus(id, status) {
  if (!['pending','approved','hidden'].includes(status)) {
    return { error: new Error(`Invalid status: ${status}`) };
  }
  const { data, error } = await supabase
    .from('reviews')
    .update({ status })
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

export async function deleteReview(id) {
  const { error } = await supabase.from('reviews').delete().eq('id', id);
  return { error };
}

export async function countPendingReviews() {
  const { count, error } = await supabase
    .from('reviews')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'pending');
  return { count: count ?? 0, error };
}

/* ── BUNDLES ── */

// Read the member list for a bundle product, joined with member product
// metadata so the editor can render names + thumbs without a second query.
export async function fetchBundleItems(bundleProductId) {
  const { data, error } = await supabase
    .from('bundle_items')
    .select(`
      id, bundle_product_id, member_product_id, member_variant_id,
      quantity, sort_order,
      product:products!bundle_items_member_product_id_fkey (
        id, name, main_image_url, photo, sale_price, price, stock_qty, has_variants
      ),
      variant:product_variants!bundle_items_member_variant_id_fkey (
        id, label, sale_price, stock_qty, main_image_url
      )
    `)
    .eq('bundle_product_id', bundleProductId)
    .order('sort_order', { ascending: true });
  return { data: data ?? [], error };
}

/**
 * Replace the member list for a bundle.
 *
 * `members`: [{ id?, member_product_id, member_variant_id?, quantity, sort_order }]
 *
 * Strategy mirrors saveProductVariants: read current, delete rows not in
 * the new set, then upsert the desired set. Caller is responsible for
 * setting products.is_bundle = true on the bundle product itself.
 */
export async function saveBundleItems(bundleProductId, members) {
  const { data: existingRows, error: readErr } = await supabase
    .from('bundle_items').select('id').eq('bundle_product_id', bundleProductId);
  if (readErr) return { error: readErr };

  const desiredIds = new Set((members || []).filter(m => m.id).map(m => m.id));
  const toDelete = (existingRows || []).filter(r => !desiredIds.has(r.id)).map(r => r.id);

  if (toDelete.length) {
    const { error } = await supabase
      .from('bundle_items').delete().in('id', toDelete);
    if (error) return { error };
  }

  // New rows (no id) and existing rows (has id) go in separate calls — see
  // saveProductVariants above for why mixing them in one upsert trips the
  // not-null constraint on id.
  const newRows = [];
  const existingRows2 = [];
  (members || []).forEach((m, idx) => {
    const row = {
      bundle_product_id: bundleProductId,
      member_product_id: parseInt(m.member_product_id, 10),
      member_variant_id: m.member_variant_id || null,
      quantity: Math.max(1, parseInt(m.quantity, 10) || 1),
      sort_order: m.sort_order != null ? parseInt(m.sort_order, 10) : idx,
    };
    if (m.id) {
      existingRows2.push({ ...row, id: m.id });
    } else {
      newRows.push(row);
    }
  });

  if (newRows.length) {
    const { error } = await supabase.from('bundle_items').insert(newRows);
    if (error) return { error };
  }

  if (existingRows2.length) {
    const { error } = await supabase
      .from('bundle_items')
      .upsert(existingRows2, { onConflict: 'id' });
    if (error) return { error };
  }

  return { error: null };
}

/* ── CSV EXPORT / IMPORT ── */

const CSV_FIELDS = [
  'id', 'name', 'slug', 'category_id', 'subtitle', 'short_description', 'description',
  'mrp', 'sale_price', 'stock_qty', 'low_stock_threshold', 'weight', 'badge',
  'main_image_url', 'gallery_image_urls', 'tags', 'meta_title', 'meta_description',
  'has_variants', 'is_bundle',
];

function csvEscape(val) {
  if (val == null) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Export all products as a CSV string. */
export function exportProductsCsv(products) {
  const header = CSV_FIELDS.join(',');
  const rows = (products || []).map(p =>
    CSV_FIELDS.map(f => {
      let val = p[f];
      if (Array.isArray(val)) val = val.join('|');
      if (typeof val === 'boolean') val = val ? 'true' : 'false';
      return csvEscape(val);
    }).join(',')
  );
  return [header, ...rows].join('\n');
}

/** Parse a CSV string into an array of product-like objects. */
export function parseProductsCsv(csvText) {
  const lines = csvText.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0]);
  return lines.slice(1).map(line => {
    const vals = parseCsvLine(line);
    const obj = {};
    headers.forEach((h, i) => {
      let v = vals[i] ?? '';
      if (h === 'gallery_image_urls' || h === 'tags') v = v ? v.split('|').filter(Boolean) : [];
      else if (h === 'has_variants' || h === 'is_bundle') v = v === 'true';
      else if (['mrp', 'sale_price', 'stock_qty', 'low_stock_threshold', 'category_id', 'id'].includes(h)) {
        v = v === '' ? null : Number(v);
      }
      obj[h] = v;
    });
    return obj;
  });
}

function parseCsvLine(line) {
  const result = [];
  let i = 0, inQuote = false, val = '';
  while (i < line.length) {
    const ch = line[i];
    if (inQuote) {
      if (ch === '"' && line[i + 1] === '"') { val += '"'; i += 2; }
      else if (ch === '"') { inQuote = false; i++; }
      else { val += ch; i++; }
    } else {
      if (ch === '"') { inQuote = true; i++; }
      else if (ch === ',') { result.push(val); val = ''; i++; }
      else { val += ch; i++; }
    }
  }
  result.push(val);
  return result;
}
